import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { colors } from "./theme.js";
// ── Context ──
const ToastContext = createContext(null);
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return ctx;
}
// ── Provider ──
const AUTO_DISMISS_MS = 3500;
const EXIT_ANIMATION_MS = 300;
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);
    const showToast = useCallback((message, type = "info") => {
        const id = nextId.current++;
        setToasts((prev) => [...prev, { id, message, type, exiting: false }]);
        setTimeout(() => {
            // Start exit animation
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
            // Remove after animation
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, EXIT_ANIMATION_MS);
        }, AUTO_DISMISS_MS);
    }, []);
    return (_jsxs(ToastContext.Provider, { value: { showToast }, children: [children, toasts.length > 0 && (_jsx("div", { style: containerStyle, children: toasts.map((toast) => (_jsx(ToastItem, { toast: toast, onDismiss: () => {
                        setToasts((prev) => prev.map((t) => (t.id === toast.id ? { ...t, exiting: true } : t)));
                        setTimeout(() => {
                            setToasts((prev) => prev.filter((t) => t.id !== toast.id));
                        }, EXIT_ANIMATION_MS);
                    } }, toast.id))) }))] }));
}
// ── Toast Item ──
const TYPE_CONFIG = {
    success: { bg: colors.successLight, border: colors.success, text: colors.success },
    error: { bg: colors.dangerLight, border: colors.danger, text: colors.danger },
    info: { bg: colors.primaryLight, border: colors.info, text: colors.primary },
};
function ToastItem({ toast, onDismiss }) {
    const config = TYPE_CONFIG[toast.type];
    return (_jsx("div", { style: {
            ...toastStyle,
            background: config.bg,
            borderLeft: `3px solid ${config.border}`,
            color: config.text,
            animation: toast.exiting
                ? `toast-exit ${EXIT_ANIMATION_MS}ms ease forwards`
                : `toast-enter 200ms ease forwards`,
        }, onClick: onDismiss, role: "alert", children: _jsx("span", { style: { flex: 1, lineHeight: 1.3 }, children: toast.message }) }));
}
// ── Styles ──
const containerStyle = {
    position: "fixed",
    bottom: 72,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "90%",
    maxWidth: 400,
    pointerEvents: "none",
};
const toastStyle = {
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: "0.82rem",
    fontWeight: 500,
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    cursor: "pointer",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
};
// ── Keyframe injection (runs once) ──
const STYLE_ID = "toast-keyframes";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
    @keyframes toast-enter {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes toast-exit {
      from { opacity: 1; transform: translateY(0); }
      to   { opacity: 0; transform: translateY(12px); }
    }
  `;
    document.head.appendChild(style);
}
